
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from '@/integrations/supabase/client';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = location.state?.from?.pathname || "/upload";
  console.log('Auth: Component mounted', { from, hasUser: !!user });

  useEffect(() => {
    if (user) {
      console.log('Auth: User detected, redirecting to:', from);
      navigate(from);
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    console.log('Auth: Starting login process');

    try {
      await signIn(email, password);
      console.log('Auth: Sign in successful, checking admin status');
      
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (roleError) {
        console.error('Auth: Error checking role:', roleError);
        toast({
          title: "Error",
          description: "Failed to verify admin status",
          variant: "destructive",
        });
        return;
      }

      console.log('Auth: Role check complete', { roleData });
      if (roleData?.role === 'admin') {
        console.log('Auth: Admin access confirmed, redirecting to:', from);
        toast({
          title: "Success",
          description: "Logged in as admin",
        });
        navigate(from);
      } else {
        console.log('Auth: Non-admin user detected');
        toast({
          title: "Access Denied",
          description: "Admin access required",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Auth: Login error:', error);
      toast({
        title: "Error",
        description: "Failed to log in. Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-new-password', {
        body: { email },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "A new password has been sent to your email.",
      });
      setResetMode(false);
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send new password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{resetMode ? "Resend Password" : "Admin Sign In"}</CardTitle>
          <CardDescription>
            {resetMode 
              ? "Enter your email to receive a new password" 
              : "Sign in with your admin credentials"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={resetMode ? handleResendPassword : handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {!resetMode && (
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="flex flex-col space-y-2">
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading 
                  ? (resetMode ? 'Sending...' : 'Signing in...') 
                  : (resetMode ? 'Send New Password' : 'Sign in')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setResetMode(!resetMode)}
              >
                {resetMode ? 'Back to Sign In' : 'Resend Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
